import { useMemo } from "react";
import { getVal } from "oolib";
import { dataVizColors, dataVizColorsText } from "../../common/colors";

interface PrepareHeatMapDataParams {
  _data: any[];
  valuePath: string | string[];
  tooltipLabelsMapping?: { [key: string]: string };
  tooltipLabelsPath?: string | string[];
  labelPath: string;
  showPercent: boolean;
  colorIdx: number;
  hatchDiagonal?: boolean; // same-field grid: row-id == column-id cells are hidden, so leave them out of the max and row totals
}

// Columns are keyed by IDENTITY, not by name — same convention as the bar
// chart's datums. The id is found by our data convention: the sibling `value`
// of each `tooltipLabelsPath` entry (children.0.display → children.0.value).
// So two columns that share a display name ("Positive" under two sub-themes)
// render as two columns instead of silently merging, and id-keyed columns are
// ordered by total count descending. A row with nothing at the value path
// falls back to label keying per cell, so data without ids (and
// tooltipLabelsMapping consumers) behaves exactly as before.
// Swap a trailing `display` segment (bare or dotted) for `sibling`.
const siblingOfDisplay = (labelPathEntry: string | undefined, sibling: string) =>
  labelPathEntry && /(^|\.)display$/.test(labelPathEntry)
    ? labelPathEntry.replace(/display$/, sibling)
    : null;

const deriveColumnKeyPath = (labelPathEntry?: string) => siblingOfDisplay(labelPathEntry, "value");

// Optional sibling `total` (children.0.display → children.0.total): the
// column's own standalone count. When the data carries it, columns order by
// it instead of by the sum of their cells, so the axes agree with rows that
// are sorted by the same count (the cooccurrence grid's same-axis case).
const deriveColumnTotalPath = (labelPathEntry?: string) => siblingOfDisplay(labelPathEntry, "total");

interface HeatMapCellData {
  labels: {
    rowValue?: string; // the row's id (sibling `value` of labelPath): diagonal detection, and what a filter matches on
    columnValue?: string; // the column's id, when the data carries one
    count: number;
    percentage?: number;
    name: string;
    tooltipLabel: string;
    dataIndex: number;
    hasData: boolean;
    barColor: string;
    textColor: string;
  };
}

interface PreparedHeatMapData {
  tableData: HeatMapCellData[][];
  columnHeaders: { key: string; displayLabel: string }[];
  dataMaxValue: number;
}

export const usePrepareHeatMapData = ({
  _data,
  valuePath,
  tooltipLabelsMapping,
  tooltipLabelsPath,
  labelPath,
  showPercent,
  colorIdx,
  hatchDiagonal = false,
}: PrepareHeatMapDataParams): PreparedHeatMapData => {

  return useMemo(() => {
    if (!_data || _data.length === 0) {
      return {
        tableData: [],
        columnHeaders: [],
        dataMaxValue: 0
      };
    }

    const valuePathArray = Array.isArray(valuePath) ? valuePath : [valuePath];
    const tooltipLabelsPathArray = tooltipLabelsPath
      ? Array.isArray(tooltipLabelsPath) ? tooltipLabelsPath : [tooltipLabelsPath]
      : null;
    const columnKeysPathArray = tooltipLabelsPathArray
      ? tooltipLabelsPathArray.map(deriveColumnKeyPath)
      : null;
    const hasColumnKeys = !!columnKeysPathArray?.some(Boolean);
    const rowKeyPath = deriveColumnKeyPath(labelPath);
    const columnTotalsPathArray = tooltipLabelsPathArray
      ? tooltipLabelsPathArray.map(deriveColumnTotalPath)
      : null;

    // Optimized: Single pass to collect all unique columns and pre-calculate row totals
    const columnMap = new Map<string, { index: number; label: string }>(); // columnKey -> order + label
    const columnTotals = new Map<string, number>(); // columnKey -> summed count (for count ordering)
    const rowTotals: number[] = [];
    const rowData: Array<{ rowName: string; rowKey?: string; cellValues: Map<string, number> }> = [];

    // First pass: collect unique columns and calculate row totals
    _data.forEach((row, rowIndex) => {
      const rowName = getVal(row, labelPath);
      const rowKeyValue = rowKeyPath ? getVal(row, rowKeyPath) : undefined;
      const rowKey = rowKeyValue !== undefined && rowKeyValue !== null ? String(rowKeyValue) : undefined;
      const cellValues = new Map<string, number>();
      let rowTotal = 0;

      valuePathArray.forEach((path, pathIndex) => {
        const value = getVal(row, path);
        if (value !== undefined && value !== null) {
          // Get column label
          let columnLabel: string;
          if (tooltipLabelsPath) {
            if (Array.isArray(tooltipLabelsPath)) {
              const labelPath = tooltipLabelsPath[pathIndex];
              if (labelPath) {
                const labelValue = getVal(row, labelPath);
                columnLabel = Array.isArray(labelValue) ? labelValue[0] : labelValue || `Column ${pathIndex}`;
              } else {
                columnLabel = `Column ${pathIndex}`;
              }
            } else {
              const labelValue = getVal(row, tooltipLabelsPath);
              columnLabel = Array.isArray(labelValue) ? labelValue[0] : labelValue || `Column ${pathIndex}`;
            }
          } else if (tooltipLabelsMapping) {
            columnLabel = tooltipLabelsMapping[path] || path;
          } else {
            columnLabel = path;
          }

          // Column identity: the id when the data carries one (same-named
          // columns stay distinct), else the label.
          let columnKey = columnLabel;
          const keyPath = columnKeysPathArray?.[pathIndex];
          if (keyPath) {
            const keyValue = getVal(row, keyPath);
            if (keyValue !== undefined && keyValue !== null && keyValue !== "") {
              columnKey = String(Array.isArray(keyValue) ? keyValue[0] : keyValue);
            }
          }

          // Only add to cellValues if this column doesn't already exist for this row
          // This prevents overwriting values when the same column appears multiple times
          if (!cellValues.has(columnKey)) {
            cellValues.set(columnKey, value);
            if (!columnMap.has(columnKey)) {
              columnMap.set(columnKey, { index: columnMap.size, label: columnLabel });
            }
            // A hatched diagonal cell is not shown, so it must not drive the
            // row's percentages or the column-sum fallback ordering.
            const hidden = hatchDiagonal && rowKey !== undefined && rowKey === columnKey;
            const totalPath = columnTotalsPathArray?.[pathIndex];
            const ownTotal = totalPath ? getVal(row, totalPath) : undefined;
            columnTotals.set(
              columnKey,
              typeof ownTotal === "number"
                ? ownTotal
                : (columnTotals.get(columnKey) || 0) + (hidden ? 0 : value)
            );
            if (!hidden) rowTotal += value;
          }
        }
      });

      rowTotals.push(rowTotal);
      rowData.push({ rowName, rowKey, cellValues });
    });

    // Create column headers. Id-keyed grids order by total count descending
    // (largest column first — what "Top N" reads as); label-keyed data keeps
    // first-seen order.
    const columnHeaders = Array.from(columnMap.entries())
      .sort(([keyA, a], [keyB, b]) =>
        hasColumnKeys
          ? (columnTotals.get(keyB) || 0) - (columnTotals.get(keyA) || 0)
          : a.index - b.index
      )
      .map(([key, { label }]) => ({
        key,
        displayLabel: label
      }));

    // Second pass: build table data efficiently
    const tableData = rowData.map(({ rowName, rowKey, cellValues }, rowIndex) => {
      const rowTotal = rowTotals[rowIndex];

      return columnHeaders.map((header, colIndex) => {
        const cellValue = cellValues.get(header.key) || 0;
        const hasData = cellValues.has(header.key);
        const percentage = rowTotal > 0 ? (cellValue / rowTotal) * 100 : 0;

        return {
          labels: {
            rowValue: rowKey,
            columnValue: hasColumnKeys ? header.key : undefined,
            count: cellValue,
            percentage: showPercent ? Math.ceil(percentage) : undefined,
            name: rowName,
            tooltipLabel: header.displayLabel,
            dataIndex: rowIndex,
            hasData,
            barColor: dataVizColors[(colorIdx + colIndex) % dataVizColors.length],
            textColor: dataVizColorsText[(colorIdx + colIndex) % dataVizColorsText.length]
          }
        };
      });
    });

    // Calculate max value efficiently. MAGNITUDE, not the largest number:
    // with negative cells (plusMinusMode) the scale has to reach as far down
    // as it does up, and for all-positive data this is the same number it
    // always was. Only over the cells that are actually shown: the hatched
    // diagonal is excluded, otherwise the standalone counts there would set
    // the colour scale and wash out every visible cell.
    let dataMaxValue = 1;
    for (const { rowKey, cellValues } of rowData) {
      for (const [key, value] of Array.from(cellValues.entries())) {
        if (hatchDiagonal && rowKey !== undefined && rowKey === key) continue;
        if (Math.abs(value) > dataMaxValue) {
          dataMaxValue = Math.abs(value);
        }
      }
    }

    return {
      tableData,
      columnHeaders,
      dataMaxValue
    };

  }, [_data, valuePath, tooltipLabelsMapping, tooltipLabelsPath, labelPath, showPercent, colorIdx]);
}; 